import type { VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { useFormFieldContext } from './form-field-context';
import { inputVariants } from './Input.variants';

export interface InputProps
  extends Omit<ComponentPropsWithRef<'input'>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Ikona po lewej stronie pola (np. lupa w wyszukiwarce). */
  leadingIcon?: ReactNode;
  /** Dowolny element po prawej — przycisk „pokaż hasło”, licznik znakow, jednostka. */
  trailingSlot?: ReactNode;
  /** Wymusza stan bledu, gdy pole nie stoi w `FormField`. */
  invalid?: boolean;
}

/** Jednowierszowe pole tekstowe. */
export function Input({
  className,
  inputSize,
  leadingIcon,
  trailingSlot,
  invalid,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  required,
  ...props
}: InputProps) {
  const field = useFormFieldContext();

  const resolvedInvalid = invalid ?? ariaInvalid ?? field?.invalid ?? false;

  const control = (
    <input
      id={id ?? field?.controlId}
      aria-describedby={ariaDescribedBy ?? field?.describedBy}
      aria-invalid={resolvedInvalid || undefined}
      required={required ?? field?.required}
      className={cn(
        inputVariants({ inputSize }),
        leadingIcon && 'pl-10',
        trailingSlot && 'pr-11',
        className,
      )}
      {...props}
    />
  );

  if (!leadingIcon && !trailingSlot) {
    return control;
  }

  return (
    <div className="relative flex items-center">
      {leadingIcon && (
        <span
          aria-hidden="true"
          className="text-fg-subtle pointer-events-none absolute left-3.5 flex items-center [&_svg]:size-4"
        >
          {leadingIcon}
        </span>
      )}

      {control}

      {trailingSlot && (
        <span className="absolute right-1.5 flex items-center">{trailingSlot}</span>
      )}
    </div>
  );
}
